import React from 'react';
import { GetServerSideProps, NextPageWithLayout } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useContextSelector } from 'use-context-selector';
import { ModalContext } from '@context/modal.context';
import extractFromContext from '@helpers/utils/extract-from-context';
import DefaultLayout from '@layouts/default-layout';
import { PriceDropProvider } from '@context/price-drop-context';
import { useRouter } from 'next/router';
import ProductService from '@services/product-service';
import { Product } from '@interfaces/models/product';
import PageAuthenticationGuard from '@components/atoms/authentication/page-authentication-guard/page-authentication-guard';
import { DropPrice } from '@components/sell/catalog/products/list/list-item/item-cta/drop-price/drop-price';
import { getAppInitialProps } from '@helpers/initial-props';
import { useBucket } from '@context/bucket.context';
import { LDState } from '@components/bucketTest/types';
import SuccessPopin from './success-popin/success-popin';
import styles from './index.module.scss';

type DropThePricePageProps = {
  product: Product;
};

const DropThePricePage: NextPageWithLayout<DropThePricePageProps> = ({ product }): React.JSX.Element => {
  const { t } = useTranslation(['default-layout', 'price-drop']);
  const { state, isFeatureEnabled } = useBucket();
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);
  const isNewPriceDropEnabled = isFeatureEnabled((f) => f.su2183PriceDropAdoption, true);
  const isListingsPageEnabled = isFeatureEnabled((f) => f.mifsListings, true);
  const redirectUrl = isListingsPageEnabled ? '/listings' : t('HEADER.LINKS.SEE_MY_SALES');
  const { query } = useRouter();
  const productId = query?.id_produit;

  const openSuccessPopin = () => {
    openModal(
      (modalDialogTitleId) => (
        <SuccessPopin
          redirectLink={redirectUrl}
          modalDialogTitleId={modalDialogTitleId}
        />
      ),
      {
        onAfterClose: () => (window.location.href = redirectUrl),
        styles: {
          content: {
            maxWidth: '550px',
          },
        },
      },
    );
  };

  if ([LDState.PENDING, LDState.INITIALIZING].includes(state)) {
    return null;
  }

  const productIdString = Array.isArray(productId) ? productId[0] : productId;

  return (
    <PageAuthenticationGuard redirectionUrl={'/'}>
      <main className={styles.priceDropContainer}>
        {productIdString && (
          <PriceDropProvider
            productId={productId as string}
            onDropPrice={openSuccessPopin}
            showBuyerFees={true}
            isNewVersion={isNewPriceDropEnabled}
          >
            <DropPrice productPath={product?.path} />
          </PriceDropProvider>
        )}
      </main>
    </PageAuthenticationGuard>
  );
};

DropThePricePage.Layout = [DefaultLayout];
DropThePricePage.PageType = 'DropThePricePage';

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { locale, serverAxiosRequestData } = extractFromContext(context);
  const initialProps = await getAppInitialProps(context);
  const translations = await serverSideTranslations(locale, [
    'default-layout',
    'vc-authentication',
    'common',
    'price-drop',
    'catalog-management',
    'a11y',
  ]);
  // temporary solution to prevent the redirection with product-id.shtml
  // remove call to getProduct, once the path is added to RecommendationsPriceResponse endpoint
  let product: Product;

  try {
    product = await ProductService.getProduct(context?.query?.id_produit as string, serverAxiosRequestData);
  } catch (e) {
    if (e.response?.status === 401) {
      return {
        redirect: {
          destination: `/login?redirect=${context.resolvedUrl}`,
          permanent: false,
        },
      };
    }
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    };
  }

  return {
    props: {
      ...initialProps,
      ...translations,
      product,
    },
  };
};

export default DropThePricePage;
